import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PackagePlus, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from './ui/PageHeader';
import DataTable, { TableToolbar } from './ui/DataTable';
import Button from './ui/Button';
import Badge from './ui/Badge';
import ActionButtons from './ui/ActionButtons';
import packageService from '../../core/services/packageService';

export default function Packages() {
  const history = useHistory();
  const [packages, setPackages] = useState([]);
  const [packageServices, setPackageServices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [serviceModal, setServiceModal] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const emptyServiceForm = { name: '', amount: '', description: '', billable: true };
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);
  const [serviceSaving, setServiceSaving] = useState(false);
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [loadingServicesFor, setLoadingServicesFor] = useState(null);

  useEffect(() => { fetchData(); }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pkgRes, svcRes] = await Promise.all([
        packageService.getAllPackages(),
        packageService.getAllPackageServices()
      ]);
      setPackages(pkgRes.data.data || []);
      setPackageServices(svcRes.data.data || []);
    } catch {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const belongsToPackage = (service, packageId) =>
    Number(service.packageId) === Number(packageId);

  const getPackageServices = (pkg) => {
    if (!pkg) return [];
    if (Array.isArray(pkg.services)) return pkg.services;
    return packageServices.filter(service => belongsToPackage(service, pkg.id));
  };

  const getServicesCount = (pkg) => getPackageServices(pkg).length;

  const getPackageTotal = (pkg) =>
    getPackageServices(pkg).reduce((acc, s) => acc + (Number(s.amount) || 0), 0);

  const getServiceSearchText = (pkg) => {
    return getPackageServices(pkg)
      .map(s => `${s.name || ''} ${s.description || ''}`.trim())
      .filter(Boolean)
      .join(' ');
  };

  const refreshPackageServices = async (pkg) => {
    setLoadingServicesFor(pkg.id);
    try {
      const { data } = await packageService.getServicesByPackage(pkg.id);
      const freshServices = data.data || [];
      setPackageServices(prev => [
        ...prev.filter(service => !belongsToPackage(service, pkg.id)),
        ...freshServices
      ]);
      setPackages(prev =>
        prev.map(item =>
          Number(item.id) === Number(pkg.id)
            ? { ...item, services: freshServices }
            : item
        )
      );
    } catch {
      toast.error('Failed to load package services');
    } finally {
      setLoadingServicesFor(null);
    }
  };

  const toggleServiceDetails = async (pkg) => {
    if (Number(expandedPackageId) === Number(pkg.id)) {
      setExpandedPackageId(null);
      return;
    }
    setExpandedPackageId(pkg.id);
    await refreshPackageServices(pkg);
  };

  const openCreateServiceModal = (pkg) => {
    setSelectedPkg(pkg);
    setServiceForm(emptyServiceForm);
    setServiceModal(true);
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setServiceSaving(true);
    try {
      await packageService.createPackageService({
        ...serviceForm,
        amount: Number(serviceForm.amount) || 0,
        packageId: selectedPkg.id
      });
      toast.success('Service added to package');
      setServiceForm(emptyServiceForm);
      setExpandedPackageId(selectedPkg.id);
      await refreshPackageServices(selectedPkg);
      setServiceModal(false);
    } catch {
      toast.error('Failed to add service');
    } finally {
      setServiceSaving(false);
    }
  };

  const packageRows = useMemo(() => {
    return packages.flatMap(pkg => {
      const packageWithServices = {
        ...pkg,
        services: getPackageServices(pkg)
      };

      if (Number(expandedPackageId) !== Number(pkg.id)) {
        return [packageWithServices];
      }

      return [
        packageWithServices,
        {
          __rowKey: `package-${pkg.id}-services`,
          __serviceDetail: true,
          package: packageWithServices,
          services: packageWithServices.services
        }
      ];
    });
  }, [packages, packageServices, expandedPackageId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await packageService.deletePackage(id);
      toast.success('Package deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete package');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Package',
      minWidth: 220,
      flex: 2,
      render: (p) => (
        <div className="profile-cell">
          <div className="avatar-sm"><PackagePlus size={18} /></div>
          <div>
            <div className="profile-cell-title">{p.name}</div>
            <div className="profile-cell-subtitle">PKG-{p.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 130,
      flex: 1,
      render: (p) => <Badge variant="purple">{p.category || 'General'}</Badge>
    },
    {
      key: 'description',
      header: 'Description',
      minWidth: 200,
      flex: 2,
      render: (p) => (
        <div className="text-truncate" style={{ maxWidth: 320 }}>
          {p.description || 'No description provided.'}
        </div>
      ),
    },
    {
      key: 'services',
      header: 'Services',
      minWidth: 160,
      flex: 1.5,
      filterValueGetter: (params) => getServiceSearchText(params.data),
      render: (p) => (
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className={`package-service-toggle ${Number(expandedPackageId) === Number(p.id) ? 'expanded' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              toggleServiceDetails(p);
            }}
            aria-expanded={Number(expandedPackageId) === Number(p.id)}
          >
            <span>{Number(expandedPackageId) === Number(p.id) ? 'Hide' : 'View'}</span>
            <Badge variant="slate">{getServicesCount(p)} Services</Badge>
          </button>
        </div>
      ),
    },
    {
      key: 'total',
      header: 'Estimate',
      minWidth: 120,
      flex: 1,
      render: (p) => <strong>Rs {getPackageTotal(p).toLocaleString()}</strong>,
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 100,
      flex: 0.8,
      align: 'right',
      render: (p) => (
        <ActionButtons
          view={false}
          onEdit={() => history.push(`/packages/edit/${p.id}`)}
          onDelete={() => handleDelete(p.id)}
        />
      ),
    },
  ];

  const renderServiceDetailRow = ({ data }) => {
    const pkg = data.package;
    const services = Array.isArray(data.services) ? data.services : [];
    const isLoading = Number(loadingServicesFor) === Number(pkg.id);

    return (
      <div className="package-service-detail-row">
        <div className="package-service-detail-card">
          <div className="package-service-detail-header">
            <div>
              <h3>{pkg.name} Services</h3>
              <p>{services.length} service{services.length === 1 ? '' : 's'} attached to this package</p>
            </div>
            <Button size="sm" onClick={() => openCreateServiceModal(pkg)}>
              + Create Service
            </Button>
          </div>

          {isLoading ? (
            <div className="package-service-detail-state">Loading services...</div>
          ) : services.length === 0 ? (
            <div className="package-service-detail-state">No services available.</div>
          ) : (
            <div className="package-service-list">
              {services.map(service => (
                <div className="package-service-item" key={service.id || service.name}>
                  <div>
                    <strong>{service.name || 'Unnamed service'}</strong>
                    {service.description && <p>{service.description}</p>}
                  </div>
                  <div className="package-service-meta">
                    <span>Rs {Number(service.amount || 0).toLocaleString()}</span>
                    <Badge variant={service.billable === false ? 'slate' : 'green'}>
                      {service.billable === false ? 'Not billable' : 'Billable'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container fade-in">
      <PageHeader
        eyebrow="Catalog"
        title="Therapy Packages"
        subtitle="Define treatment packages, service pricing, and package categories."
        actions={
          <Button onClick={() => history.push('/packages/new')}>
            <Plus size={17} /> Create Package
          </Button>
        }
      />

      <DataTable
        title="Package Catalog"
        subtitle="Search and manage therapy packages and service estimates."
        loading={loading}
        rows={packageRows}
        columns={columns}
        empty="No therapy packages found."
        isFullWidthRow={(params) => Boolean(params.rowNode?.data?.__serviceDetail)}
        fullWidthCellRenderer={renderServiceDetailRow}
        getRowHeight={(params) => {
          if (!params.data?.__serviceDetail) return 68;
          const servicesCount = Array.isArray(params.data.services) ? params.data.services.length : 0;
          return Math.min(360, 132 + Math.max(1, servicesCount) * 66);
        }}
        toolbar={
          <TableToolbar
            search={search}
            onSearch={setSearch}
            placeholder="Search by package name or category..."
          />
        }
      />

      {serviceModal && (
        <div className="modal-overlay">
          <div className="modal package-service-modal">
            <div className="modal-header">
              <h2>Create Service <span>{selectedPkg?.name}</span></h2>
              <button type="button" className="modal-close" onClick={() => setServiceModal(false)}>x</button>
            </div>
            <form onSubmit={handleAddService}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Service Name*</label>
                    <input
                      className="form-control"
                      value={serviceForm.name}
                      onChange={e => setServiceForm({ ...serviceForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount (Rs)*</label>
                    <input
                      type="number"
                      className="form-control"
                      value={serviceForm.amount}
                      onChange={e => setServiceForm({ ...serviceForm, amount: e.target.value })}
                      min="0"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={serviceForm.description}
                    onChange={e => setServiceForm({ ...serviceForm, description: e.target.value })}
                  />
                </div>
                <div className="form-check">
                  <input
                    id="serviceBillable"
                    className="form-check-input"
                    type="checkbox"
                    checked={serviceForm.billable}
                    onChange={e => setServiceForm({ ...serviceForm, billable: e.target.checked })}
                  />
                  <label htmlFor="serviceBillable">Billable service</label>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setServiceModal(false)}>Cancel</Button>
                <Button type="submit" loading={serviceSaving}>Create Service</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Styles in ./styles/appointments.css */}

    </div>
  );
}